import type { GameCatalogEntry } from '@shared/types';

interface HeroCarouselProps {
  featured: GameCatalogEntry[];
  onSelect: (entry: GameCatalogEntry) => void;
  onCreatePrivate?: (entry: GameCatalogEntry) => void;
  onViewStadium?: (entry: GameCatalogEntry) => void;
}

/**
 * Hero/Banner superior del Hub (similar al "Spotlight" de Roblox).
 * Muestra un slide grande con CTAs:
 *  - "Jugar ahora"        → `onSelect` (sala pública abierta o nueva).
 *  - "Crear sala privada" → `onCreatePrivate` (sala nueva con código).
 *  - "Ver trailer"        → `onViewStadium` (navega a la vista 3D pública).
 *
 * En Fase 3 el fondo del slide se reemplaza por un modelo 3D en vivo.
 */
export const HeroCarousel = ({ featured, onSelect, onCreatePrivate, onViewStadium }: HeroCarouselProps) => {
  if (featured.length === 0) {
    return (
      <div className="grid h-64 place-items-center rounded-2xl border border-dashed border-white/10 bg-ink-800/40 text-slate-400">
        Cargando destacados…
      </div>
    );
  }

  const [first] = featured;
  if (!first) return null;

  return (
    <section
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-pitch-gradient shadow-card"
      aria-label="Destacado"
    >
      <div className="absolute inset-0 bg-gradient-to-r from-ink-900 via-ink-900/70 to-transparent" />
      <div className="relative grid grid-cols-1 gap-6 p-8 md:grid-cols-2">
        <div className="flex flex-col justify-center gap-4">
          <span className="pill w-fit bg-accent-500/20 text-accent-400">
            ⭐ Destacado de hoy
          </span>
          <h1 className="text-display text-4xl font-bold leading-tight text-white md:text-5xl">
            {first.title}
          </h1>
          <p className="max-w-md text-base text-slate-300">{first.description}</p>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => onSelect(first)} className="btn-primary h-11 px-6">
              Jugar ahora
            </button>
            {onCreatePrivate && (
              <button onClick={() => onCreatePrivate(first)} className="btn-ghost h-11 px-6">
                🔒 Crear sala privada
              </button>
            )}
            {onViewStadium && (
              <button
                onClick={() => onViewStadium(first)}
                className="btn-ghost h-11 px-6"
                aria-label="Ver vista 3D del estadio"
              >
                Ver estadio 3D
              </button>
            )}
          </div>
          <p className="text-xs text-slate-500">
            Si la conexión con el servidor falla, igualmente puedes explorar la cancha 3D.
          </p>
        </div>
        <div className="relative hidden h-72 md:block">
          <div className="absolute inset-0 grid place-items-center text-9xl opacity-40">
            {first.sport === 'futbol' && '⚽'}
            {first.sport === 'beisbol' && '⚾'}
            {first.sport === 'basquetbol' && '🏀'}
          </div>
        </div>
      </div>
    </section>
  );
};
